import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import {
  AlertOctagon, AlertTriangle, Info, ShieldOff, ChevronRight,
  RefreshCw, HelpCircle, CheckCircle2,
} from "lucide-react";

// Read-only Rule Conflict Report.
// Renders the /api/automation/conflicts envelope grouped by severity, with
// deep-link buttons that reuse the  highlight-row pattern.
// Strictly read-only: no apply / fix / approve affordances.

const SEV_META = {
  automation_blocking: {
    Icon: ShieldOff, tone: "text-purple-700", chip: "bg-purple-100 text-purple-800",
    label: "Blocks automation", rank: 0,
  },
  critical: {
    Icon: AlertOctagon, tone: "text-red-600", chip: "bg-red-100 text-red-700",
    label: "Needs attention", rank: 1,
  },
  warn: {
    Icon: AlertTriangle, tone: "text-amber-600", chip: "bg-amber-100 text-amber-800",
    label: "Worth reviewing", rank: 2,
  },
  info: {
    Icon: Info, tone: "text-[#2563EB]", chip: "bg-[#DBE7FE] text-[#1D4ED8]",
    label: "Tip", rank: 3,
  },
};

const CATEGORY_LABELS = {
  workforce_availability: "Staffing",
  assignment_rules: "Assignments",
  leave_rules: "Leave",
  fairness_rules: "Fair distribution",
  location_rules: "Locations",
  kpi_foundations: "KPIs",
  data_completeness: "Data quality",
};
const prettyCategory = (k) => CATEGORY_LABELS[k] || k;

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(1, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function ConflictRow({ c }) {
  const m = SEV_META[c.severity] || SEV_META.info;
  const { Icon } = m;
  return (
    <li className="py-3 border-b border-slate-100 last:border-0"
        data-testid={`conflict-row-${c.signature}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${m.tone}`}
              data-testid={`conflict-severity-${c.signature}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded ${m.chip}`}>
              {m.label}
            </span>
            {c.category && (
              <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded bg-slate-100 text-[#4B5773]"
                data-testid={`conflict-category-${c.signature}`}>
                {prettyCategory(c.category)}
              </span>
            )}
            <span className="text-sm font-medium text-[#0B1A36]">{c.title}</span>
          </div>
          {c.explain?.length > 0 && (
            <ul className="mt-1 list-disc list-outside ml-4 text-xs text-[#4B5773] space-y-0.5">
              {c.explain.slice(0, 3).map((e, i) =>
                <li key={i} data-testid={`conflict-explain-${c.signature}-${i}`}>{e}</li>)}
            </ul>
          )}
          {(c.affected?.length > 0 || c.sources?.length > 0) && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {(c.affected || []).slice(0, 4).map((a, i) => (
                <span key={`a${i}`}
                  data-testid={`conflict-affected-${c.signature}-${i}`}
                  className="inline-flex items-center text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-[#0B1A36]">
                  {a.kind}: {a.name || a.id}
                </span>
              ))}
            </div>
          )}
          {c.suggested_next_action && (
            <div className="mt-1.5 text-[11px] text-[#4B5773]"
                  data-testid={`conflict-suggestion-${c.signature}`}>
              <strong className="text-[#0B1A36]">Suggested:</strong> {c.suggested_next_action}
            </div>
          )}
        </div>
        {c.action?.href && (
          <Link to={c.action.href}
                data-testid={`conflict-action-${c.signature}`}
                className="text-xs text-[#2563EB] hover:underline inline-flex items-center shrink-0">
            {c.action.label || "Open"}
            <ChevronRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    </li>
  );
}

export default function ConflictReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [helpOpen, setHelpOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/automation/conflicts");
      setData(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.conflicts;
    return data.conflicts.filter((c) => c.severity === filter);
  }, [data, filter]);

  if (!data) {
    return (
      <Card title="Setup issues">
        <div className="text-sm text-[#4B5773] py-6 text-center">Loading…</div>
      </Card>
    );
  }

  return (
    <Card title={null}>
      <div data-testid="conflict-report">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-[#0B1A36]">
              Setup issues
            </h2>
            <div className="text-sm text-[#4B5773]">
              {data.total === 0
                ? "Nothing to flag, your setup is looking healthy."
                : `${data.total} item${data.total !== 1 ? "s" : ""} worth reviewing`}
            </div>
            <p className="text-[11px] text-[#4B5773] mt-1 max-w-2xl">
              Anything in your setup that might cause a problem, for example, a coverage policy
              without enough qualified people, or a skill no employee has. Each item is read-only;
              you decide what to fix.
            </p>
            <div className="flex items-center gap-3 mt-1 text-[11px] text-[#4B5773]"
                  data-testid="conflict-summary">
              <span className="text-purple-700">
                {data.summary.automation_blocking} blocking
              </span>
              <span className="text-red-600">{data.summary.critical} critical</span>
              <span className="text-amber-600">{data.summary.warn} warn</span>
              <span className="text-[#2563EB]">{data.summary.info} info</span>
              <span>Last computed {timeAgo(data.computed_at)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["all", "automation_blocking", "critical", "warn", "info"].map((s) => (
              <button key={s}
                onClick={() => setFilter(s)}
                data-testid={`conflict-filter-${s}`}
                className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                  filter === s
                    ? "border-[#2563EB] bg-[#DBE7FE] text-[#1D4ED8]"
                    : "border-slate-200 text-[#4B5773] hover:bg-slate-50"
                }`}>
                {s === "all" ? "All" : SEV_META[s].label}
              </button>
            ))}
            <Button variant="outline" size="sm" onClick={load}
              disabled={busy} data-testid="conflict-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <button type="button" onClick={() => setHelpOpen((v) => !v)}
              data-testid="conflict-help"
              className="text-[#4B5773] hover:text-[#0B1A36]"
              aria-label="What does this measure?">
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>

        {helpOpen && (
          <p data-testid="conflict-help-text"
              className="mt-3 p-3 rounded-md bg-[#F6F9FE] border border-slate-200 text-xs text-[#4B5773]">
            {data.what_it_measures}
          </p>
        )}

        {filtered.length === 0 ? (
          <div className="mt-5 py-6 text-center text-sm text-emerald-700">
            <CheckCircle2 className="h-6 w-6 mx-auto mb-2 opacity-80" />
            {data.total === 0
              ? "Nothing to flag right now"
              : "Nothing matches this filter"}
          </div>
        ) : (
          <ul className="mt-5 border border-slate-200 rounded-lg px-3">
            {filtered.map((c) => <ConflictRow key={c.signature} c={c} />)}
          </ul>
        )}
      </div>
    </Card>
  );
}
